import {CloseOutlined} from "@ant-design/icons";
import {Button, Drawer, message} from "antd";
import {useState} from "react";
import OTPInput from "react-otp-input";
import {QRCodeSVG} from "qrcode.react";
import styled from "styled-components";
import {useActivateTotp} from "../../../react-query-hooks/two-factor";
import {StyledOtpInput} from "../../payouts/v2/tabs/payout-settings-tab/otp-view/styles";

const StyledTotpSetupDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    height: 100vh !important;
  }
`;

const StyledTotpOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    min-width: 0;
    height: 6.4rem !important;
    border-radius: 8px;
    font-family: Inter;
    font-weight: 400;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0;
    text-align: center;
  }
`;

const formatSecretForDisplay = (secret?: string) =>
  secret?.match(/.{1,4}/g)?.join(" ") ?? "";

interface TotpSetupDrawerProps {
  visible: boolean;
  qrUri?: string;
  secret?: string;
  onClose: () => void;
  onSuccess: (backupCodes: string[]) => void;
}

export function TotpSetupDrawer({
  visible,
  qrUri,
  secret,
  onClose,
  onSuccess,
}: TotpSetupDrawerProps) {
  const [otp, setOtp] = useState("");
  const activateTotpMutation = useActivateTotp();

  const handleCopy = () => {
    if (!secret) {
      return;
    }
    navigator.clipboard.writeText(secret);
    message.success("Setup key copied to clipboard");
  };

  const handleSubmit = () => {
    activateTotpMutation.mutate(
      {twofaCode: otp},
      {
        onSuccess: (response: any) => {
          setOtp("");
          onSuccess(response.data.backupCodes);
        },
        onError: () => setOtp(""),
      }
    );
  };

  return (
    <StyledTotpSetupDrawer
      title={
        <div
          style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}
        >
          <span>Authenticator app</span>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              width: 24,
              height: 24,
              padding: 4,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </div>
      }
      closable={false}
      placement="right"
      width={685}
      open={visible}
      onClose={onClose}
      footer={
        <Button
          size="small"
          type="primary"
          block
          disabled={otp.length < 6}
          loading={activateTotpMutation.isLoading}
          onClick={handleSubmit}
        >
          Verify code
        </Button>
      }
      footerStyle={{padding: "16px 24px"}}
    >
      <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
        <div>
          <h3 style={{margin: 0}}>Scan this QR code</h3>
          <p style={{margin: 0, paddingTop: 12}}>
            Install an authenticator app in your phone and scan the code below
          </p>
          <div style={{display: "flex", justifyContent: "center", marginTop: 24}}>
            {qrUri && <QRCodeSVG value={qrUri} size={200} />}
          </div>
        </div>

        <div style={{display: "flex", flexDirection: "column", gap: "16px"}}>
          <p style={{margin: 0}}>Can&apos;t scan? Enter this setup key manually</p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#F5F5F5",
              padding: "16px 20px",
              borderRadius: 16,
            }}
          >
            <span
              style={{
                flex: 1,
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "22px",
                color: "#1C1726",
              }}
            >
              {formatSecretForDisplay(secret)}
            </span>
            <Button size="small" type="text" onClick={handleCopy}>
              Copy
            </Button>
          </div>
        </div>

        <div style={{display: "flex", flexDirection: "column", gap: "16px"}}>
          <h3 style={{margin: 0}}>Enter the 6-digit code from your authenticator app</h3>
          <OTPInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            shouldAutoFocus
            inputType="tel"
            containerStyle={{display: "flex", gap: "12px", width: "100%"}}
            renderInput={(props) => <StyledTotpOtpInput {...props} />}
          />
        </div>
      </div>
    </StyledTotpSetupDrawer>
  );
}
